import styles from './Memos.module.css';

import { useState } from 'react';
import { DateTime } from 'luxon';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';

import StickyNote2 from '@mui/icons-material/StickyNote2';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import Cancel from '@mui/icons-material/Cancel';
import Warning from '@mui/icons-material/Warning';
import Info from '@mui/icons-material/Info';
import CheckCircle from '@mui/icons-material/CheckCircle';

export const Memos = ({ patrollerId, shiftId, onSave, memoData = [] }) => {
    const [ memos, setMemos ] = useState(memoData);
    const [ newMemo, setNewMemo ] = useState({
        patrollerId: patrollerId || '',
        date: DateTime.now().toISODate(),
        time: DateTime.now().toFormat('HH:mm'),
        memo: '',
        priority: 'Normal',
        category: 'General'
    });
    const [ isAdding, setIsAdding ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ isSaving, setIsSaving ] = useState(false);

    // Priority levels
    const priorityLevels = [
        { value: 'Low', color: 'info', icon: Info },
        { value: 'Normal', color: 'default', icon: CheckCircle },
        { value: 'High', color: 'warning', icon: Warning },
        { value: 'Critical', color: 'error', icon: Warning }
    ];

    // Memo categories
    const categories = [
        'General',
        'Safety',
        'Equipment',
        'Weather',
        'Incident',
        'Maintenance',
        'Communication',
        'Training',
        'Emergency',
        'Other'
    ];

    const handleNewMemoChange = (field, value) => {
        setNewMemo(prev => ({
            ...prev,
            [ field ]: value
        }));
        setError(null);
    };

    const handleAddMemo = () => {
        if (!newMemo.memo.trim()) {
            setError('Memo content is required');
            return;
        }

        const memoToAdd = {
            ...newMemo,
            id: `memo_${ Date.now() }`,
            shiftId: shiftId,
            date: new Date(newMemo.date).getTime(),
            time: DateTime.fromFormat(newMemo.time, 'HH:mm').toMillis(),
            timestamp: Date.now()
        };

        setMemos(prev => [ memoToAdd, ...prev ]);
        setNewMemo({
            patrollerId: patrollerId || '',
            date: DateTime.now().toISODate(),
            time: DateTime.now().toFormat('HH:mm'),
            memo: '',
            priority: 'Normal',
            category: 'General'
        });
        setIsAdding(false);
        setError(null);
    };

    const handleRemoveMemo = (memoId) => {
        setMemos(prev => prev.filter(memo => memo.id !== memoId));
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        setError(null);

        try {
            await onSave(memos);
        } catch (err) {
            console.error('Failed to save memos:', err);
            setError('Failed to save memos: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const getPriorityInfo = (priority) => {
        return priorityLevels.find(p => p.value === priority) || priorityLevels[ 1 ];
    };

    const getMemosByPriority = (priority) => {
        return memos.filter(memo => memo.priority === priority);
    };

    const sortedMemos = [ ...memos ].sort((a, b) => {
        // Sort by priority first (Critical > High > Normal > Low), then by timestamp
        const priorityOrder = { 'Critical': 4, 'High': 3, 'Normal': 2, 'Low': 1 };
        const aPriority = priorityOrder[ a.priority ] || 2;
        const bPriority = priorityOrder[ b.priority ] || 2;

        if (aPriority !== bPriority) {
            return bPriority - aPriority;
        }

        return b.timestamp - a.timestamp;
    });

    const formatMemoTime = (memo) => {
        const date = DateTime.fromMillis(memo.date);
        const time = DateTime.fromMillis(memo.time);
        return `${ date.toFormat('MMM dd') } at ${ time.toFormat('HH:mm') }`;
    };

    return (
        <Card className={ styles.memoCard }>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={ 1 }>
                        <StickyNote2 />
                        <Typography variant="h6">Memos</Typography>
                        <Chip
                            label={ `${ memos.length } memo${ memos.length !== 1 ? 's' : '' }` }
                            color="primary"
                            size="small"
                        />
                    </Box>
                }
                action={
                    <Box display="flex" gap={ 1 }>
                        <Button
                            startIcon={ <Add /> }
                            onClick={ () => setIsAdding(!isAdding) }
                            size="small"
                        >
                            { isAdding ? 'Cancel' : 'Add Memo' }
                        </Button>
                        { memos.length > 0 && (
                            <Button
                                variant="contained"
                                startIcon={ <Save /> }
                                onClick={ handleSaveAll }
                                disabled={ isSaving }
                                size="small"
                            >
                                { isSaving ? 'Saving...' : 'Save All' }
                            </Button>
                        ) }
                    </Box>
                }
            />

            <CardContent>
                { error && (
                    <Alert variant='filled' severity="error" className={ styles.errorAlert }>
                        { error }
                    </Alert>
                ) }

                {/* Add New Memo Form */ }
                { isAdding && (
                    <Card variant="outlined" className={ styles.addForm }>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                                Add New Memo
                            </Typography>
                            <Grid container spacing={ 2 }>
                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <TextField
                                        fullWidth
                                        label="Date"
                                        type="date"
                                        value={ newMemo.date }
                                        onChange={ (e) => handleNewMemoChange('date', e.target.value) }
                                        InputLabelProps={ { shrink: true } }
                                    />
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <TextField
                                        fullWidth
                                        label="Time"
                                        type="time"
                                        value={ newMemo.time }
                                        onChange={ (e) => handleNewMemoChange('time', e.target.value) }
                                        InputLabelProps={ { shrink: true } }
                                    />
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <FormControl fullWidth>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            value={ newMemo.category }
                                            label="Category"
                                            onChange={ (e) => handleNewMemoChange('category', e.target.value) }
                                        >
                                            { categories.map((category) => (
                                                <MenuItem key={ category } value={ category }>
                                                    { category }
                                                </MenuItem>
                                            )) }
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <FormControl fullWidth>
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            value={ newMemo.priority }
                                            label="Priority"
                                            onChange={ (e) => handleNewMemoChange('priority', e.target.value) }
                                        >
                                            { priorityLevels.map((priority) => (
                                                <MenuItem key={ priority.value } value={ priority.value }>
                                                    <Box display="flex" alignItems="center" gap={ 1 }>
                                                        <priority.icon />
                                                        { priority.value }
                                                    </Box>
                                                </MenuItem>
                                            )) }
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={ { xs: 12 } }>
                                    <TextField
                                        fullWidth
                                        label="Memo Content *"
                                        multiline
                                        rows={ 3 }
                                        value={ newMemo.memo }
                                        onChange={ (e) => handleNewMemoChange('memo', e.target.value) }
                                        placeholder="Enter memo details..."
                                    />
                                </Grid>

                                <Grid size={ { xs: 12 } }>
                                    <Box display="flex" gap={ 1 } justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            startIcon={ <Cancel /> }
                                            onClick={ () => {
                                                setIsAdding(false);
                                                setError(null);
                                            } }
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={ <Add /> }
                                            onClick={ handleAddMemo }
                                        >
                                            Add Memo
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ) }

                {/* Priority Summary */ }
                { memos.length > 0 && (
                    <Box className={ styles.prioritySummary }>
                        <Grid container spacing={ 2 }>
                            { priorityLevels.map((priority) => {
                                const count = getMemosByPriority(priority.value).length;
                                return (
                                    <Grid item xs={ 6 } sm={ 3 } key={ priority.value }>
                                        <Paper className={ styles.priorityCard }>
                                            <Box display="flex" alignItems="center" gap={ 1 }>
                                                <priority.icon
                                                    className={ styles[ `priority-${ priority.color }` ] }
                                                />
                                                <Box>
                                                    <Typography variant="h6">{ count }</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        { priority.value }
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            }) }
                        </Grid>
                    </Box>
                ) }

                {/* Memo List */ }
                { sortedMemos.length > 0 ? (
                    <List className={ styles.memoList }>
                        { sortedMemos.map((memo, index) => {
                            const priorityInfo = getPriorityInfo(memo.priority);
                            return (
                                <Box key={ memo.id }>
                                    <ListItem className={ styles.memoItem }>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
                                                    <Chip
                                                        icon={ <priorityInfo.icon /> }
                                                        label={ memo.priority }
                                                        color={ priorityInfo.color }
                                                        size="small"
                                                    />
                                                    <Chip
                                                        label={ memo.category }
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        { formatMemoTime(memo) }
                                                    </Typography>
                                                </Box>
                                            }

                                            secondary={
                                                <Typography variant="body2" className={ styles.memoContent }>
                                                    { memo.memo }
                                                </Typography>
                                            }

                                            secondaryAction={
                                                <IconButton
                                                    size="small"
                                                    onClick={ () => handleRemoveMemo(memo.id) }
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            }
                                        />
                                    </ListItem>
                                    { index < sortedMemos.length - 1 && <Divider /> }
                                </Box>
                            );
                        }) }
                    </List>
                ) : (
                    <Box className={ styles.emptyState }>
                        <Typography variant="body1" color="text.secondary">
                            No memos recorded yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Click &ldquo;Add Memo&rdquo; to start documenting shift communications.
                        </Typography>
                    </Box>
                ) }
            </CardContent>
        </Card>
    );
};